import { createClient } from '@deepgram/sdk';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useResolvedLanguage } from '@plato/ui-legacy/lib/hooks/locale';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../../utils/api.ts';
import { parseDeepgramResponse, type TranscriptionResult } from '../lib/deepgram-response.ts';

export type TranscriptionStage = 'token' | 'network' | 'deepgram';

class TranscriptionError extends Error {
    public readonly stage: TranscriptionStage;
    public readonly statusCode: number | undefined;

    public constructor(stage: TranscriptionStage, message: string, statusCode?: number) {
        super(message);
        this.name = 'TranscriptionError';
        this.stage = stage;
        this.statusCode = statusCode;
    }
}

type Recording = { blob: Blob; recordingDurationMs: number };

/** What the client knew about the request, regardless of outcome. */
export type TranscriptionRequest = {
    recordingDurationMs: number;
    mimeType: string;
    blobSizeBytes: number;
    languageMode: string;
};

export type Transcription = TranscriptionResult & TranscriptionRequest & { latencyMs: number };

export type TranscriptionFailure = TranscriptionRequest & { stage: TranscriptionStage; statusCode?: number };

type Callbacks = {
    onTranscribed: (transcription: Transcription) => void;
    onFailed: (failure: TranscriptionFailure) => void;
};

export function useDeepgram({ onTranscribed, onFailed }: Callbacks): {
    isTranscribing: boolean;
    transcribe: (recording: Recording) => void;
} {
    const language = useResolvedLanguage();
    const { enableMultilingualVoiceRecording } = useAppConfig();

    // 'multi' lets nova-3 code-switch across de/en/fr within one recording;
    // a fixed language drops speech in every other language.
    const languageMode = enableMultilingualVoiceRecording ? 'multi' : language;

    const { refetch: fetchToken } = api.platoUser.command.voiceRecording.getDeepgramTempToken.useQuery(
        undefined,
        {
            enabled: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    const describe = ({ blob, recordingDurationMs }: Recording): TranscriptionRequest => ({
        recordingDurationMs,
        mimeType: blob.type,
        blobSizeBytes: blob.size,
        languageMode,
    });

    const { isPending: isTranscribing, mutate: transcribe } = useMutation({
        mutationFn: async (recording: Recording): Promise<Transcription> => {
            const startedAt = performance.now();
            const { data } = await fetchToken();

            if (!data?.accessToken) {
                throw new TranscriptionError('token', 'Failed to receive voice recording token');
            }

            const url = new URL(createClient({ accessToken: data.accessToken }).getRequestUrl('v1/listen'));
            url.searchParams.set('language', languageMode);
            url.searchParams.set('model', 'nova-3');
            url.searchParams.set('punctuate', 'true');
            url.searchParams.set('smart_format', 'true');
            url.searchParams.set('numerals', 'true');
            url.searchParams.set('tag', data.usageTag);

            let response: Response;
            try {
                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${data.accessToken}`,
                        'Content-Type': 'deepgram/audio+video',
                    },
                    body: recording.blob,
                });
            } catch (error) {
                throw new TranscriptionError('network', `Deepgram request failed: ${String(error)}`);
            }

            if (!response.ok) {
                const body = await response.text();
                throw new TranscriptionError(
                    'deepgram',
                    `Failed to transcribe audio. Response status: ${response.status}. Body: ${body}`,
                    response.status,
                );
            }

            // The connection can still drop while the body downloads; that is a network
            // failure, not a Deepgram one.
            let body: string;
            try {
                body = await response.text();
            } catch (error) {
                throw new TranscriptionError(
                    'network',
                    `Deepgram response body failed to download: ${String(error)}`,
                );
            }

            let result: TranscriptionResult;
            try {
                result = parseDeepgramResponse(JSON.parse(body));
            } catch (error) {
                throw new TranscriptionError('deepgram', `Unexpected Deepgram response: ${String(error)}`);
            }

            return {
                ...result,
                ...describe(recording),
                latencyMs: Math.round(performance.now() - startedAt),
            };
        },
        onSuccess: onTranscribed,
        onError: (error, recording) => {
            const request = describe(recording);
            const stage = error instanceof TranscriptionError ? error.stage : 'deepgram';
            const statusCode = error instanceof TranscriptionError ? error.statusCode : undefined;

            // Network drops are expected in the field and already reach PostHog with their
            // stage; only failures on our side or Deepgram's belong in Sentry.
            if (stage !== 'network') {
                captureException(error, { extra: { ...request, stage, statusCode } });
            }
            onFailed({ ...request, stage, statusCode });
        },
    });

    return {
        isTranscribing,
        transcribe,
    };
}
